import React, { useCallback, useEffect, useState } from "react";
import {
  DetailsList,
  IStackStyles,
  SearchBox,
  Stack,
  Text,
  SelectionMode,
  IColumn,
  Icon,
  IconButton,
  TooltipHost,
  Sticky,
  IDetailsHeaderProps,
  DetailsRow,
} from "@fluentui/react";
import { api } from "../../services";
import { buttonDisable, buttonRestore, list } from "./styles";
import { show } from "../../components";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

const container: Partial<IStackStyles> = {
  root: {
    width: "980px",
    margin: "32px auto !important",
  },
};

const rowItem: Partial<IStackStyles> = {
  root: {
    alignItems: "center",
  },
};

interface IItem {
  id: string;
  name: string;
  address: string;
  deleted: string | null;
}

const Clients: React.FC = () => {
  const [clients, setClients] = useState<IItem[] | undefined>();
  const [filterTerm, setFilterTerm] = useState<string | undefined>();
  const navigate = useNavigate();

  function handleEdit(id?: string) {
    show({
      title: "Attention",
      subText: "Are you sure you want to edit?",
      onPositiveButton: () => {
        navigate(`/edit/client/${id}`);
      },
    });
  }

  function handleDisable(id?: string) {
    show({
      title: "Attention",
      subText: "Are you sure you want to state this item?",
      onPositiveButton: async () => {
        try {
          await api.client().deleteById(id);
          const oldClients = [...(clients || [])];

          oldClients.forEach((client) => {
            if (client.id === id && !client.deleted) {
              client.deleted = new Date().toLocaleDateString();
            } else if (client.id === id && client.deleted) {
              client.deleted = null;
            }
          });

          setClients(oldClients);
        } catch (error) {
          let message = "Error, try again later.";

          if (error instanceof AxiosError) {
            message = error.response?.data.message ?? "Error, try again later.";
          }

          toast.error(message);
        }
      },
    });
  }

  const columns: IColumn[] = [
    {
      key: "name",
      name: "Name",
      minWidth: 200,
      fieldName: "name",
      onRender: (item: any) => {
        return (
          <Stack styles={rowItem} tokens={{ childrenGap: 16 }} horizontal>
            <Icon iconName="Contact" />
            <Text>{item?.name}</Text>
          </Stack>
        );
      },
    },
    {
      key: "address",
      name: "Address",
      minWidth: 200,
      fieldName: "address",
    },
    {
      key: "actions",
      name: "Actions",
      minWidth: 150,
      onRender: (item: any) => {
        return (
          <Stack horizontal styles={rowItem}>
            <TooltipHost content="Edit User">
              <IconButton
                disabled={!!item?.deleted}
                onClick={() => handleEdit(item?.id)}
                iconProps={{ iconName: "EditContact" }}
              />
            </TooltipHost>
            <TooltipHost
              content={item?.deleted ? "Restore User" : "Disable User"}
            >
              <IconButton
                onClick={() => handleDisable(item?.id)}
                styles={item?.deleted ? buttonRestore : buttonDisable}
                iconProps={{
                  iconName: !!!item?.deleted ? "UserRemove" : "UserFollowed",
                }}
              />
            </TooltipHost>
          </Stack>
        );
      },
    },
  ];

  const getUsers = useCallback(async () => {
    try {
      const { data } = await api.client().get();

      if (data) {
        setClients(
          data.map((client) => ({
            id: client.id,
            name: client.name,
            address: client.address,
            deleted: client.deleted,
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  function handleFilter(item: IItem) {
    if (!filterTerm) {
      return true;
    }
    const byUserName = item.address
      .toUpperCase()
      .includes(filterTerm?.toUpperCase());
    const byName = item.name.toUpperCase().includes(filterTerm?.toUpperCase());

    if (byUserName || byName) {
      return true;
    }

    return false;
  }

  return (
    <Stack styles={container}>
      <Text variant="xxLarge">Clients List</Text>
      <SearchBox
        onChange={(_, value) => setFilterTerm(value)}
        placeholder="Search"
      />
      {clients && (
        <DetailsList
          styles={list}
          items={clients.filter(handleFilter)}
          columns={columns}
          selectionMode={SelectionMode.none}
          isHeaderVisible={true}
          //@ts-ignore
          onRenderRow={(props: IDetailsRowProps) => {
            return <DetailsRow disabled={!!props.item.deleted} {...props} />;
          }}
          //@ts-ignore
          onRenderDetailsHeader={
            // tslint:disable-next-line:jsx-no-lambda
            (
              detailsHeaderProps: IDetailsHeaderProps,
              //@ts-ignore
              defaultRender: IRenderFunction<IDetailsHeaderProps>
            ) => <Sticky>{defaultRender(detailsHeaderProps)}</Sticky>
          }
        />
      )}
    </Stack>
  );
};

export { Clients };
